import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["elementTitle", "type", "propsToDisplay"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import isNumber from 'lodash.isnumber';
import ReactDOMServer from 'react-dom/server';
import { Link } from '@blaze-cms/nextjs-components';
import { ApolloProvider } from '@apollo/client';
import { buildPropsToDisplayWithContent, summaryComponents, getClassModifiers } from '@blaze-cms/react-page-builder';
import getPath from './get-path';
import { MARKER, POLYLINE, POLYGON, RECTANGLE, CIRCLE, COLOR, CLICK, HEADING_LARGE, LIST, DEFAULT_CUSTOM_MARKER_PATH } from '../../constants';
const shapesArray = [];
const infoWindowArray = [];
const clearShapes = () => {
  if (shapesArray.length) {
    shapesArray.forEach(shape => shape.setMap(null));
    shapesArray.length = 0;
  }
};
const closeInfoWindows = () => {
  if (infoWindowArray.length) {
    infoWindowArray.forEach(infoWindow => infoWindow.close());
    infoWindowArray.length = 0;
  }
};
const addShapeListeners = (map, router, shape, imageData, shapeInstance, client) => {
  const {
    enableInfoPane = false,
    dataSummaries = [],
    url,
    shapeData
  } = shape;
  if (enableInfoPane) {
    const infoWindow = new window.google.maps.InfoWindow({
      content: ''
    });
    shapeInstance.addListener(CLICK, async event => {
      closeInfoWindows();
      const content = ReactDOMServer.renderToString(await infoWindowContent(imageData, dataSummaries, url, shapeData, client));
      infoWindow.setContent(content);
      infoWindow.setPosition(event.latLng);
      infoWindow.open(map, shapeInstance);
      infoWindowArray.push(infoWindow);
    });
  } else if (url) {
    shapeInstance.addListener(CLICK, () => {
      router.push(url);
    });
  }
};
const setStyleOptions = shape => {
  const {
    strokeColor,
    strokeOpacity,
    strokeWeight,
    fillColor,
    fillOpacity
  } = shape;
  const styleSettings = {
    strokeColor,
    fillColor,
    strokeOpacity,
    strokeWeight,
    fillOpacity
  };
  return _objectSpread({}, Object.keys(styleSettings).reduce((acc, key) => {
    const settingValue = styleSettings[key];
    const shouldValueBeNumber = !key.includes(COLOR);
    const hasValuePassedCondition = shouldValueBeNumber ? isNumber(settingValue) : settingValue;
    if (hasValuePassedCondition) acc[key] = settingValue;
    return acc;
  }, {}));
};
const areRectangleBoundsNumbers = bounds => bounds.every(bound => isNumber(bound));
const isPositionCorrect = (lat, lng) => isNumber(lat) && isNumber(lng);
const getIconUrl = (imageId, imageData) => {
  if (!imageId) return '';
  const image = imageData.find(({
    id
  }) => id === imageId);
  if (!image || !image.url) return '';
  return image.url;
};
const infoWindowContent = async (imageData, dataSummaries, url, shapeData, client) => {
  const summariesWithContent = await Promise.all(dataSummaries.map(async dataSummary => {
    const {
        elementTitle,
        type,
        propsToDisplay
      } = dataSummary,
      otherProps = _objectWithoutProperties(dataSummary, _excluded);
    const isTypeHeading = type === HEADING_LARGE;
    const summaryPropsWithContent = buildPropsToDisplayWithContent(shapeData, propsToDisplay);
    const modifier = getClassModifiers(LIST, otherProps);
    const SummaryComponent = summaryComponents[type];
    await SummaryComponent.render.preload(); // preload dynamic nextjs component

    if (!summaryPropsWithContent.length) return null;
    const getDataSummaryComponent = () => {
      const component = /*#__PURE__*/React.createElement(ApolloProvider, {
        client: client
      }, /*#__PURE__*/React.createElement(SummaryComponent, {
        key: type,
        modifier: modifier,
        elementTitle: elementTitle,
        summaryPropsWithContent: summaryPropsWithContent
      }));
      if (isTypeHeading && url) return /*#__PURE__*/React.createElement(Link, {
        href: url
      }, component);
      return component;
    };
    return getDataSummaryComponent();
  }));
  const {
    imageId
  } = shapeData || {};
  const image = imageData.find(({
    id
  }) => id === imageId);
  const shouldShowImage = image && image.url;
  const getImageElement = () => {
    const {
      url: imageUrl,
      data
    } = image;
    const imageElement = /*#__PURE__*/React.createElement("img", {
      className: "info__window",
      src: imageUrl,
      alt: data && data.altText ? data.altText : ''
    });
    if (url) return /*#__PURE__*/React.createElement(Link, {
      href: url
    }, imageElement);
    return imageElement;
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, shouldShowImage && getImageElement(), summariesWithContent);
};
function getIcon(imageData, shape) {
  const {
    imageId,
    markerWidth = 50,
    markerHeight = 50,
    strokeColor,
    strokeOpacity,
    strokeWeight,
    fillColor,
    fillOpacity,
    svgPath
  } = shape;
  const iconUrl = getIconUrl(imageId, imageData);
  const icon = iconUrl ? {
    url: iconUrl,
    scaledSize: new window.google.maps.Size(markerWidth || 50, markerHeight || 50)
  } : null;
  if (icon) return icon;
  if (!svgPath && !fillColor && !strokeColor) return {}; // any can be true for custom marker

  const svgMarker = {
    path: svgPath || DEFAULT_CUSTOM_MARKER_PATH,
    strokeColor,
    strokeOpacity,
    strokeWeight,
    fillColor,
    fillOpacity,
    rotation: 0,
    scale: 2
  };
  return svgMarker;
}
const setMarker = (map, router, shape, imageData, client) => {
  const {
    label = '',
    latitude,
    longitude
  } = shape;
  if (isPositionCorrect(latitude, longitude)) {
    const svgMarker = getIcon(imageData, shape);
    const marker = new window.google.maps.Marker({
      position: {
        lat: latitude,
        lng: longitude
      },
      map,
      icon: svgMarker,
      title: label || ''
    });
    addShapeListeners(map, router, shape, imageData, marker, client);
    shapesArray.push(marker);
    marker.setMap(map);
  }
};
const setPolyline = (map, router, shape, imageData) => {
  const {
    paths
  } = shape;
  const path = getPath(paths);
  if (path.length) {
    const polyline = new window.google.maps.Polyline(_objectSpread({
      path,
      geodesic: true
    }, setStyleOptions(shape)));
    addShapeListeners(map, router, shape, imageData, polyline);
    shapesArray.push(polyline);
    polyline.setMap(map);
  }
};
const setPolygon = (map, router, shape, imageData) => {
  const {
    paths: unParsedPaths
  } = shape;
  const paths = getPath(unParsedPaths);
  if (paths.length) {
    const polygon = new window.google.maps.Polygon(_objectSpread({
      paths
    }, setStyleOptions(shape)));
    addShapeListeners(map, router, shape, imageData, polygon);
    shapesArray.push(polygon);
    polygon.setMap(map);
  }
};
const setRectangle = (map, router, shape, imageData) => {
  const {
    northBound,
    southBound,
    eastBound,
    westBound
  } = shape;
  if (areRectangleBoundsNumbers([northBound, southBound, eastBound, westBound])) {
    const rectangle = new window.google.maps.Rectangle(_objectSpread(_objectSpread({}, setStyleOptions(shape)), {}, {
      map,
      bounds: {
        north: northBound,
        south: southBound,
        east: eastBound,
        west: westBound
      }
    }));
    addShapeListeners(map, router, shape, imageData, rectangle);
    shapesArray.push(rectangle);
    rectangle.setMap(map);
  }
};
const setCircle = (map, router, shape, imageData) => {
  const {
    latitude,
    longitude,
    radius
  } = shape;
  if (isPositionCorrect(latitude, longitude) && isNumber(radius)) {
    const circle = new window.google.maps.Circle(_objectSpread(_objectSpread({}, setStyleOptions(shape)), {}, {
      map,
      center: {
        lat: latitude,
        lng: longitude
      },
      radius
    }));
    addShapeListeners(map, router, shape, imageData, circle);
    shapesArray.push(circle);
    circle.setMap(map);
  }
};
const setShapes = (map, router, shapes, imageData, client) => {
  const mapZoom = map.getZoom();
  clearShapes();
  shapes.forEach(shape => {
    const {
      type,
      minZoom,
      maxZoom
    } = shape;
    if (mapZoom >= minZoom && mapZoom <= maxZoom) {
      switch (type) {
        case MARKER:
          {
            setMarker(map, router, shape, imageData, client);
            break;
          }
        case POLYLINE:
          {
            setPolyline(map, router, shape, imageData);
            break;
          }
        case POLYGON:
          {
            setPolygon(map, router, shape, imageData);
            break;
          }
        case RECTANGLE:
          {
            setRectangle(map, router, shape, imageData);
            break;
          }
        case CIRCLE:
          {
            setCircle(map, router, shape, imageData);
            break;
          }
        default:
          break;
      }
    }
  });
};
export default setShapes;
//# sourceMappingURL=set-shapes.js.map